import type { MetricSeriesResponse } from "@central-hub/contracts";
import { useEffect, useState } from "react";
import { api } from "./api";
import type { Session } from "./auth";
import { ago, dayClock, duration } from "./format";
import { Icon } from "./icons";
import {
  datacenterName,
  type ServerBoard,
  type ServerRow,
  type StockState,
  stockHistory,
} from "./model";

/** The week of availability changes slowly: refresh it every 5 minutes. */
const HISTORY_MS = 5 * 60_000;

const STATE_LABEL: Record<StockState, string> = {
  available: "Available",
  out: "Out of stock",
  unknown: "Can't tell",
};

/**
 * The servers view: hardware watched because it sells out (the `ovh` module), whether it can
 * be ordered now and where, since when, and the last week of availability per datacenter.
 */
export function ServersView({
  board,
  session,
  now,
  stale,
}: {
  board: ServerBoard | null;
  session: Session;
  now: number;
  stale: boolean;
}) {
  const history = useStockHistory(session, board !== null);
  if (!board) {
    return (
      <main className="view">
        <p className="muted">
          No servers are watched: enable the <code>ovh</code> module with the plans to watch.
        </p>
      </main>
    );
  }
  return (
    <main className="view" data-stale={stale}>
      <ul className="tally" aria-label="Servers at a glance">
        <li className="tally-item" data-status={board.available > 0 ? "up" : "unknown"}>
          <Icon name={board.available > 0 ? "up" : "unknown"} />
          <b className="num">{board.available}</b>
          <span>
            of {board.rows.length} watched {board.rows.length === 1 ? "server" : "servers"} can be
            ordered now
          </span>
        </li>
      </ul>

      <section className="panel" aria-labelledby="servers-title">
        <div className="panel-head">
          <h2 id="servers-title" className="eyebrow">
            Watched servers
          </h2>
          <span className="num">a notification when one can be ordered</span>
        </div>
        <ul className="servers">
          {board.rows.map((row) => (
            <ServerItem key={`${row.target}/${row.check}`} row={row} now={now} history={history} />
          ))}
        </ul>
      </section>
    </main>
  );
}

function ServerItem({
  row,
  now,
  history,
}: {
  row: ServerRow;
  now: number;
  history: MetricSeriesResponse | null;
}) {
  return (
    <li className="server" data-status={row.tone}>
      <div className="server-head">
        <Icon name={row.tone} />
        <b className="server-name">{row.label}</b>
        <span className="server-state">{STATE_LABEL[row.state]}</span>
        {row.since !== null && (
          <span className="muted" title={dayClock(row.since)}>
            for {duration(now - row.since)}
          </span>
        )}
        {row.orderUrl && (
          <a className="server-order" href={row.orderUrl} target="_blank" rel="noopener noreferrer">
            Order page
          </a>
        )}
      </div>
      <p className="server-meta muted">
        <span className="mono">{row.planCode}</span> · checked {ago(now - row.checkedAt)}
        {row.state === "unknown" && row.message && <> · {row.message}</>}
      </p>
      <ul className="server-dcs">
        {row.datacenters.map((dc) => (
          <li key={dc.code} data-status={dc.available ? "up" : "unknown"}>
            <span className="server-dc">
              <b className="mono">{dc.code}</b> {datacenterName(dc.code)}
            </span>
            <span className="server-dc-state">
              {dc.available ? `available${dc.estimate ? ` · ${dc.estimate}` : ""}` : "out of stock"}
            </span>
            {history && <WeekStrip slots={stockHistory(history, row.watch, dc.code)} />}
          </li>
        ))}
      </ul>
    </li>
  );
}

/** One cell per hour of the last week: green when it could be ordered at some point. */
function WeekStrip({ slots }: { slots: ReturnType<typeof stockHistory> }) {
  const seen = slots.filter((s) => s.status !== "none").length;
  const up = slots.filter((s) => s.status === "up").length;
  return (
    <span
      className="server-week"
      role="img"
      aria-label={`Last 7 days: available ${up} of ${seen} hours checked`}
      title={`Last 7 days: available ${up} of ${seen} hours checked`}
    >
      {slots.map((s) => (
        <span key={s.t} data-status={s.status} />
      ))}
    </span>
  );
}

/** The week of `ovh_server_available`, while the view is open; null until (or unless) it loads. */
function useStockHistory(session: Session, enabled: boolean): MetricSeriesResponse | null {
  const [series, setSeries] = useState<MetricSeriesResponse | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const token = await session.getToken();
        if (token) {
          const response = await api.metricSeries(token, "ovh_server_available", "7d");
          if (!stopped) setSeries(response);
        }
      } catch {
        // No history rather than a broken one: the state above is current.
      }
      if (!stopped) timer = setTimeout(load, HISTORY_MS);
    };
    load();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [session, enabled]);
  return series;
}
